import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AccountUserLite, GroupSummary, avatarIntent, createGroupApi, deleteGroupApi, groupDeletionApi,
  initialsOf, listGroupsApi, updateGroupApi,
} from '../../api/access';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { Modal, RowMenu, TableCard } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { PHONE, useMediaQuery } from '../../shared/media';

/**
 * Settings → Groups.
 *
 * A group is a named set of the account's people, so a grant can be handed to
 * "Field techs" once instead of to five individuals who then drift apart as
 * people join and leave.
 *
 * "Group", never "Team" — `plans.key = 'team'` already displays as "Team", and
 * "you are on the Team plan, add them to a Team" is a sentence nobody should
 * have to read.
 */

/** ONE editor at a time (rule 6). */
type Editor =
  | { kind: 'add' }
  | { kind: 'rename' | 'people' | 'delete'; id: number }
  | null;

export default function GroupsAdmin() {
  const [groups, setGroups] = useState<GroupSummary[] | null>(null);
  const [users, setUsers] = useState<AccountUserLite[]>([]);
  const [error, setError] = useState('');
  const toast = useToast();
  const [editor, setEditor] = useState<Editor>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  /** a card per group instead of the 5-column table (§4G G2) */
  const phone = useMediaQuery(PHONE);

  const load = () => listGroupsApi()
    .then((r) => { setGroups(r.groups); setUsers(r.users); })
    .catch((e) => setError(e.message));

  useEffect(() => { load(); }, []);

  const editing = editor && 'id' in editor ? groups?.find((g) => g.id === editor.id) || null : null;

  async function write(fn: () => Promise<unknown>, doneMsg?: string) {
    setError('');
    try {
      await fn();
      setEditor(null);
      if (doneMsg) toast(doneMsg);
      load();
    } catch (e: any) { setError(e.message); }
  }

  if (!groups) return <div className="fs-card pad">{error ? <div className="ad-err">{error}</div> : 'Loading…'}</div>;

  return (
    <div className="st-stack">
      <div className="fs-page-head">
        <div className="text">
          <h1>Groups</h1>
          <div className="fs-page-sub">
            {groups.length
              ? `${groups.length} ${groups.length === 1 ? 'group' : 'groups'} · ${users.length} people in the account`
              : 'A named set of people, so a folder can be shared with all of them at once.'}
          </div>
        </div>
        <button className="fs-btn primary" onClick={() => setEditor({ kind: 'add' })}>New group</button>
      </div>

      {error && <div className="ad-err">{error}</div>}

      {!groups.length ? (
        <div className="fs-card pad st-empty">
          <b style={{ fontSize: 'var(--text-card-title)' }}>No groups yet</b>
          <div className="fs-cell-sub" style={{ maxWidth: 460, margin: '8px auto 0', lineHeight: 1.6 }}>
            A group lets you share a folder with five people once, instead of five
            times — and when somebody joins the team, adding them to the group gives
            them everything the group can reach.
          </div>
        </div>
      ) : (
        <TableCard
          footer={(
            <span>
              A grant to a group reaches everybody in it. Owners and Admins reach every
              form regardless, so putting one in a group changes nothing for them.
            </span>
          )}
        >
          {phone ? (
            <div className="gr-cards">
              {groups.map((g) => (
                <div className="gr-card" key={g.id}>
                  <div className="head">
                    <div className="who">
                      <div className="name">{g.name}</div>
                      <div className="facts" style={{ marginTop: 2 }}>
                        {g.member_count} {g.member_count === 1 ? 'person' : 'people'}
                      </div>
                    </div>
                    <RowMenu open={openMenuId === g.id} onToggle={(o) => setOpenMenuId(o ? g.id : null)} label={g.name}>
                      <button onClick={() => setEditor({ kind: 'rename', id: g.id })}>Rename…</button>
                      <button onClick={() => setEditor({ kind: 'people', id: g.id })}>Manage people…</button>
                      <div className="rule" />
                      <button className="danger" onClick={() => setEditor({ kind: 'delete', id: g.id })}>
                        Delete group…
                      </button>
                    </RowMenu>
                  </div>
                  {!!g.members.length && (
                    <div className="pills">
                      <Avatars members={g.members} />
                    </div>
                  )}
                  <div className="facts">
                    {g.folder_count || g.form_count ? (
                      <>
                        reaches {g.folder_count} {g.folder_count === 1 ? 'folder' : 'folders'}
                        {' · '}{g.form_count} {g.form_count === 1 ? 'form' : 'forms'}
                      </>
                    ) : (
                      <>
                        <span className="fs-pill warn">nothing yet</span>{' '}
                        <Link to="/admin">Share a folder with them →</Link>
                      </>
                    )}
                    {' · created '}{new Date(g.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <table className="fs-table">
            <thead>
              <tr>
                <th>Group</th><th>People</th><th>Reaches</th><th>Created</th>
                <th style={{ width: 52 }} />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{g.name}</div>
                    <div className="fs-cell-sub">
                      {g.member_count} {g.member_count === 1 ? 'person' : 'people'}
                    </div>
                  </td>
                  <td>
                    {g.members.length
                      ? <Avatars members={g.members} />
                      : <span className="fs-cell-sub faint">nobody yet</span>}
                  </td>
                  <td>
                    {g.folder_count || g.form_count ? (
                      <>
                        <span style={{ fontWeight: 600 }}>
                          {g.folder_count} {g.folder_count === 1 ? 'folder' : 'folders'}
                          {' · '}
                          {g.form_count} {g.form_count === 1 ? 'form' : 'forms'}
                        </span>
                        <div className="fs-cell-sub">shared with this group</div>
                      </>
                    ) : (
                      <>
                        {/* warn, because warn means "needs a human" — and a
                            group nothing is shared with does nothing at all. */}
                        <span className="fs-pill warn">nothing yet</span>
                        <div className="fs-cell-sub">
                          <Link to="/admin">Share a folder with them →</Link>
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>
                    {new Date(g.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <RowMenu open={openMenuId === g.id} onToggle={(o) => setOpenMenuId(o ? g.id : null)}>
                      <button onClick={() => setEditor({ kind: 'rename', id: g.id })}>Rename…</button>
                      <button onClick={() => setEditor({ kind: 'people', id: g.id })}>Manage people…</button>
                      <div className="rule" />
                      <button className="danger" onClick={() => setEditor({ kind: 'delete', id: g.id })}>
                        Delete group…
                      </button>
                    </RowMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </TableCard>
      )}

      {editor?.kind === 'add' && (
        <GroupModal
          users={users}
          onClose={() => setEditor(null)}
          onDone={(name, ids) => write(() => createGroupApi(name, ids), `Created ${name}.`)}
        />
      )}
      {editor?.kind === 'rename' && editing && (
        <GroupModal
          group={editing} users={users} nameOnly
          onClose={() => setEditor(null)}
          onDone={(name) => write(() => updateGroupApi(editing.id, { name }))}
        />
      )}
      {editor?.kind === 'people' && editing && (
        <GroupModal
          group={editing} users={users} peopleOnly
          onClose={() => setEditor(null)}
          onDone={(_name, ids) => write(() => updateGroupApi(editing.id, { userIds: ids }))}
        />
      )}
      {editor?.kind === 'delete' && editing && (
        <DeleteGroupModal
          group={editing}
          onClose={() => setEditor(null)}
          onConfirm={() => write(() => deleteGroupApi(editing.id), `Deleted ${editing.name}.`)}
        />
      )}
    </div>
  );
}

/**
 * One modal for creating, renaming and choosing people, because they are the
 * same two fields in different combinations.
 *
 * Admins and deactivated people appear in the list, VISIBLE AND INERT with the
 * reason on the row — never quietly missing. An Admin already reaches every
 * form, so putting one in a group would change nothing, and a list that
 * silently omitted them would look like a bug.
 */
function GroupModal({ group, users, nameOnly, peopleOnly, onClose, onDone }: {
  group?: GroupSummary;
  users: AccountUserLite[];
  nameOnly?: boolean;
  peopleOnly?: boolean;
  onClose: () => void;
  onDone: (name: string, userIds: number[]) => void;
}) {
  const [name, setName] = useState(group?.name || '');
  const [picked, setPicked] = useState<Set<number>>(new Set(group?.members.map((m) => m.id) || []));
  const [limit, setLimit] = useState<PlanLimitBody | null>(null);

  const title = group ? (peopleOnly ? `People in ${group.name}` : `Rename ${group.name}`) : 'New group';
  const changed = nameOnly
    ? name.trim() && name.trim() !== group?.name
    : !!name.trim();

  function toggle(id: number) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }

  async function save() {
    try { onDone(name.trim(), [...picked]); }
    catch (e: any) {
      const l = planLimitOf(e);
      if (l) setLimit(l);
    }
  }

  return (
    <Modal
      title={title} narrow onClose={onClose}
      footer={<>
        <button className="fs-btn primary" onClick={save} disabled={!peopleOnly && !changed}>
          {group ? 'Save' : 'Create group'}
        </button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      {!peopleOnly && (
        <div className="fs-field">
          <label htmlFor="gr-name">Group name</label>
          <input id="gr-name" autoFocus value={name} maxLength={60}
            onChange={(e) => setName(e.target.value)} placeholder="Field techs" />
        </div>
      )}

      {!nameOnly && (
        <>
          <div className="fs-field" style={{ marginTop: peopleOnly ? 0 : 16 }}>
            <label>People</label>
          </div>
          {users.map((u) => {
            const bypasses = u.role === 'owner' || u.role === 'admin';
            const inert = bypasses || !u.active;
            return (
              <label
                key={u.id}
                className="fs-choice"
                aria-disabled={inert || undefined}
                style={inert ? { opacity: 0.6 } : undefined}
              >
                <input
                  type="checkbox"
                  checked={picked.has(u.id)}
                  disabled={inert}
                  onChange={() => toggle(u.id)}
                />
                <span>
                  <b>{u.name}</b>
                  <small>
                    {u.email}
                    {bypasses && ` · an ${u.role === 'owner' ? 'Owner' : 'Admin'} already reaches every form`}
                    {!u.active && ' · deactivated'}
                  </small>
                </span>
              </label>
            );
          })}
          <div className="fs-cell-sub" style={{ marginTop: 10 }}>
            {picked.size} {picked.size === 1 ? 'person' : 'people'} selected. Everyone in the
            group reaches whatever the group is shared with.
          </div>
        </>
      )}

      {limit && <div style={{ marginTop: 14 }}><PlanLimitBlock body={limit} /></div>}
    </Modal>
  );
}

/** Delete states the loss with counts — a dialog, not a `window.confirm`. */
function DeleteGroupModal({ group, onClose, onConfirm }: {
  group: GroupSummary;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [info, setInfo] = useState<{ memberCount: number; folderCount: number; formCount: number } | null>(null);

  useEffect(() => { groupDeletionApi(group.id).then(setInfo).catch(() => undefined); }, [group.id]);

  return (
    <Modal
      title={<>Delete the <b>{group.name}</b> group?</>} narrow onClose={onClose}
      footer={<>
        <button className="fs-btn danger" onClick={onConfirm}>Delete group</button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      <div className="fs-consequence warn">
        {info ? (
          <>
            <b>
              {info.memberCount} {info.memberCount === 1 ? 'person' : 'people'} lose the access
              this group was giving them
            </b>
            {' — '}
            {info.folderCount} {info.folderCount === 1 ? 'folder' : 'folders'} and{' '}
            {info.formCount} {info.formCount === 1 ? 'form' : 'forms'}.
            {' '}Nobody is removed from the account, and anything shared with them
            directly is untouched.
          </>
        ) : 'Working out what this group reaches…'}
      </div>
    </Modal>
  );
}

/**
 * The first four people in a group, then "+n".
 *
 * Extracted so the phone card (§4G G2) shows the same stack the table cell
 * shows rather than a second one that could drift from it.
 */
function Avatars({ members }: { members: GroupSummary['members'] }) {
  return (
    <span className="fs-avatar-stack">
      {members.slice(0, 4).map((m) => {
        const name = m.name || m.email;
        const intent = avatarIntent(name);
        return (
          <span
            key={m.id}
            className={`fs-avatar${m.active ? '' : ' inactive'}`}
            title={`${name}${m.active ? '' : ' · deactivated'}`}
            style={{ background: `var(--${intent}-bg)`, color: `var(--${intent}-fg)` }}
          >
            {initialsOf(name)}
          </span>
        );
      })}
      {members.length > 4 && (
        <span className="fs-avatar" style={{ background: 'var(--neutral-bg)', color: 'var(--neutral-fg)' }}>
          +{members.length - 4}
        </span>
      )}
    </span>
  );
}
